import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { MetricCard } from "@/components/MetricCard";
import { ResponsiveMiniLineChart } from "@/components/ResponsiveMiniLineChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiClient } from "@/lib/api";
import { DecisionCard } from "@/pages/user/components/DecisionCard";
import { ExplainabilityDrawer } from "@/pages/user/components/ExplainabilityDrawer";
import { UserLearningImpactWidget } from "@/pages/user/components/UserLearningImpactWidget";

const toToastMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "string" && first.trim()) {
      return first;
    }
    if (first && typeof first === "object") {
      return String(first.msg || first.message || first.type || fallback);
    }
  }
  if (detail && typeof detail === "object") {
    return String(detail.msg || detail.message || detail.code || fallback);
  }
  const message = error?.message;
  if (typeof message === "string" && message.trim()) {
    return message;
  }
  return fallback;
};

const settleWithTimeout = (promise, timeoutMs = 20000) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      // Stage 5I: JS-level timeout only. HTTP-level abort ise bir sonraki load()
      // cycle basladiginda master AbortController uzerinden yapilir. Bu sekilde
      // tek yavas endpoint diger 13 endpoint'i cascade abort etmez.
      window.setTimeout(() => reject(new Error(`request_timeout_${timeoutMs}`)), timeoutMs);
    }),
  ]);

const computeRiskPolicyHealth = (policy) => {
  if (!policy) {
    return { score: 0, level: "UNKNOWN", message: "Risk policy bulunamadı" };
  }

  let score = 0;
  const positionSize = Number(policy.position_size_pct || 0);
  const dailyLoss = Number(policy.daily_loss_cutoff_pct || 0);
  const maxOpen = Number(policy.max_open_positions || 0);
  const maxLeverage = Number(policy.max_leverage || 0);
  const spreadLimit = Number(policy.spread_limit_bps || 0);
  const slippageLimit = Number(policy.slippage_limit_bps || 0);
  const minLiquidity = Number(policy.min_liquidity_usdt || 0);

  score += positionSize <= 1.5 ? 25 : positionSize <= 2.5 ? 18 : positionSize <= 4 ? 10 : 4;
  score += dailyLoss <= 3 ? 25 : dailyLoss <= 5 ? 18 : dailyLoss <= 8 ? 10 : 4;
  score += maxOpen <= 2 ? 20 : maxOpen <= 4 ? 14 : maxOpen <= 6 ? 8 : 4;
  score += maxLeverage <= 2 ? 15 : maxLeverage <= 3 ? 10 : maxLeverage <= 5 ? 6 : 2;
  score += spreadLimit <= 30 && slippageLimit <= 40 && minLiquidity >= 100000 ? 15 : 8;

  const normalized = Math.max(0, Math.min(100, score));
  if (normalized >= 80) {
    return { score: normalized, level: "SAFE", message: "Koruyucu ve dengeli" };
  }
  if (normalized >= 60) {
    return { score: normalized, level: "BALANCED", message: "Dengeli fakat optimize edilebilir" };
  }
  return { score: normalized, level: "AGGRESSIVE", message: "Risk seviyesi yüksek" };
};

export const UserDashboardPage = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [livePositions, setLivePositions] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [riskPolicies, setRiskPolicies] = useState([]);
  // NİHAİ GÖREV EMRİ (2026-07-28/29, Madde 27) — hangi dashboard veri
  // kaynaklarının SON fetch'te BAŞARISIZ olduğu izlenir; ilgili metrik
  // kartları bu durumda "0" yerine "Veri alınamadı" gösterir (sahte sıfır
  // değeri gerçek veriymiş gibi SUNULMAZ).
  const [failedDataSources, setFailedDataSources] = useState([]);
  const [signalMode, setSignalMode] = useState(null);
  const [botProfiles, setBotProfiles] = useState([]);
  const [recentSignals, setRecentSignals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [wizardForm, setWizardForm] = useState(null);
  const [isSavingWizard, setIsSavingWizard] = useState(false);
  const [wizardDismissed, setWizardDismissed] = useState(false);
  const [isControlBusy, setIsControlBusy] = useState(false);
  const [decisionCards, setDecisionCards] = useState([]);
  const [strategyScoreboard, setStrategyScoreboard] = useState(null);
  const [strategyEfficiency, setStrategyEfficiency] = useState(null);
  const [exitQuality, setExitQuality] = useState(null);
  // 2026-06-02 BUG-B fix: Risk Policy Health Score artık backend'ten dinamik gelir
  // (statik config + canlı durum). Sabit "84" davranışı sona erdi.
  const [riskHealthBackend, setRiskHealthBackend] = useState(null);
  const [signalFunnel, setSignalFunnel] = useState(null);
  const [rejectRootCauses, setRejectRootCauses] = useState(null);
  const [selectedDecisionSymbol, setSelectedDecisionSymbol] = useState("");
  const [isExplainabilityDrawerOpen, setIsExplainabilityDrawerOpen] = useState(false);
  const [symbolExplainability, setSymbolExplainability] = useState(null);
  const [isExplainabilityLoading, setIsExplainabilityLoading] = useState(false);
  const [impactStrategyId, setImpactStrategyId] = useState("");
  const [impactFamily, setImpactFamily] = useState("");

  // Stage 5I fix: overlap guard + AbortController for polling storm on /user/overview.
  // Root cause: settleWithTimeout only rejects the JS promise; underlying axios XHR
  // stays pending in browser + reverse-proxy queue. We now abort in-flight requests
  // before starting a new load() tick, and also on unmount / route change.
  const inFlightRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortRef = useRef(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const formatDateLabel = (value) => {
    if (!value) {
      return "-";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "-";
    }
    return parsed.toLocaleString("tr-TR");
  };

  const loadSymbolExplainability = useCallback(async (symbol) => {
    if (!symbol) {
      setSymbolExplainability(null);
      return;
    }
    setIsExplainabilityLoading(true);
    try {
      const { data } = await apiClient.get(`/user/explainability/${encodeURIComponent(symbol)}`);
      setSymbolExplainability(data || null);
    } catch (error) {
      setSymbolExplainability(null);
      toast.error(toToastMessage(error, "Explainability yüklenemedi"));
    } finally {
      setIsExplainabilityLoading(false);
    }
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    // Stage 5I overlap guard: previous poll still running -> skip this tick
    // to prevent reverse proxy queue buildup and 504 cascades.
    if (inFlightRef.current) {
      return;
    }
    // Also abort any lingering XHRs from a prior manual reload before starting new ones.
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = controller.signal;
    inFlightRef.current = true;
    if (!silent) {
      setIsLoading(true);
    }
    try {
      const settled = await Promise.allSettled([
        settleWithTimeout(apiClient.get("/user/dashboard", { signal })),
        settleWithTimeout(apiClient.get("/user/portfolio", { signal })),
        settleWithTimeout(apiClient.get("/user/live/positions", { params: { limit: 1 }, signal })),
        settleWithTimeout(apiClient.get("/user/performance", { signal })),
        settleWithTimeout(apiClient.get("/risk-policies", { signal })),
        settleWithTimeout(apiClient.get("/bot-profiles", { signal })),
        settleWithTimeout(apiClient.get("/user/signals", { params: { limit: 50 }, signal })),
        settleWithTimeout(apiClient.get("/user/decision-cards", { params: { limit: 12 }, signal })),
        settleWithTimeout(apiClient.get("/user/live/strategy-scoreboard", { params: { window: "7d" }, signal })),
        settleWithTimeout(apiClient.get("/user/live/strategy-efficiency", { params: { window: "7d" }, signal })),
        settleWithTimeout(apiClient.get("/user/live/exit-quality", { params: { window: "7d" }, signal })),
        settleWithTimeout(apiClient.get("/user/live/signal-funnel", { params: { window: "7d" }, signal })),
        settleWithTimeout(apiClient.get("/user/live/reject-root-causes", { params: { window: "7d" }, signal })),
        // 2026-06-02 BUG-B fix: dinamik risk policy health score
        settleWithTimeout(apiClient.get("/user/risk-policy-health", { signal })),
      ]);

      const endpointNames = [
        "user_dashboard",
        "user_portfolio",
        "user_live_positions",
        "user_performance",
        "risk_policies",
        "bot_profiles",
        "user_signals",
        "decision_cards",
        "strategy_scoreboard",
        "strategy_efficiency",
        "exit_quality",
        "signal_funnel",
        "reject_root_causes",
        "risk_policy_health",
      ];
      const failedEndpoints = [];
      const readData = (index, fallback) => {
        const result = settled[index];
        if (result?.status === "fulfilled") {
          return result.value?.data ?? fallback;
        }
        failedEndpoints.push(endpointNames[index]);
        return fallback;
      };

      const dashboardData = readData(0, {
        bot_count: 0,
        running_bot_count: 0,
        risk_policy_count: 0,
        current_capital: 0,
        available_balance: 0,
        open_positions_count: 0,
        pending_signals_count: 0,
        heartbeat: null,
      });
      const portfolioData = readData(1, {
        current_capital: 0,
        available_balance: 0,
        live_status: "pure_live",
        closed_pnl: 0,
      });
      const livePositionsData = readData(2, { total_unrealized_pnl: 0, total_position_notional: 0, positions: [] });
      const performanceData = readData(3, {
        win_rate: 0,
        closed_trades: 0,
      });
      const riskPoliciesData = readData(4, []);
      const botProfilesData = readData(5, []);
      const recentSignalsData = readData(6, []);
      const decisionCardsData = readData(7, { items: [] });
      const strategyScoreboardData = readData(8, { items: [], worst_strategies: [] });
      const strategyEfficiencyData = readData(9, { rankings: {}, items: [] });
      const exitQualityData = readData(10, {});
      const signalFunnelData = readData(11, {});
      const rejectRootCauseData = readData(12, { by_reason: [], by_strategy: [], by_symbol: [], by_day: [] });
      const riskHealthData = readData(13, null);

      if (!isMountedRef.current) {
        return;
      }

      setDashboard(dashboardData);
      setPortfolio(portfolioData);
      setLivePositions(livePositionsData || null);
      setPerformance(performanceData);
      setRiskPolicies(riskPoliciesData || []);
      const inferredMode = String((Array.isArray(recentSignalsData) && recentSignalsData.length > 0 ? recentSignalsData[0]?.mode : "AUTO") || "AUTO").toUpperCase();
      setSignalMode({ mode: inferredMode });
      setBotProfiles(botProfilesData || []);
      setRecentSignals(recentSignalsData || []);
      const cards = decisionCardsData?.items || [];
      setDecisionCards(cards);
      setStrategyScoreboard(strategyScoreboardData || null);
      setStrategyEfficiency(strategyEfficiencyData || null);
      setExitQuality(exitQualityData || null);
      setSignalFunnel(signalFunnelData || null);
      setRejectRootCauses(rejectRootCauseData || null);
      setRiskHealthBackend(riskHealthData);
      setFailedDataSources(failedEndpoints);

      if (failedEndpoints.length > 0 && !silent) {
        // Stage 5I: cold-start latency backend yavas oldugunda 14 endpoint'in cogu
        // 12sn settleWithTimeout'a takilabilir. Partial-fail thresholdu: yarisindan
        // fazlasi basarisiz olursa toast, aksi halde sessiz gec ve mevcut fallback'lerle render et.
        if (failedEndpoints.length >= 8) {
          toast.error(`Bazı dashboard kaynakları yüklenemedi: ${failedEndpoints.join(", ")}`);
        }
      }

      if (cards.length > 0) {
        setSelectedDecisionSymbol((prev) => prev || cards[0].symbol || "");
        setImpactStrategyId((prev) => prev || cards[0]?.top_contributors?.[0]?.strategy_id || "");
        setImpactFamily((prev) => prev || cards[0]?.dominant_family || "");
      } else {
        setSelectedDecisionSymbol("");
        setSymbolExplainability(null);
      }
    } catch (error) {
      if (!silent && isMountedRef.current) {
        toast.error(toToastMessage(error, "Dashboard yüklenemedi"));
      }
    } finally {
      inFlightRef.current = false;
      // Not: abortRef.current'u null'lamiyoruz. Bu sayede bir sonraki cycle
      // basladiginda `if (abortRef.current) abortRef.current.abort()` guard'i
      // hala pending olan XHR'lari HTTP-level cancel eder (overlap-free polling).
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Stage 5I: initial cold-start yuklemesini silent yaparak backend cold-start
    // gecikmesinde 'Bazi kaynaklari yuklenemedi' toast bombardimanini onluyoruz.
    // Skeleton fallback data ile kapaniyor (setIsLoading(false) her durumda cagrilir).
    load({ silent: true });
  }, [load]);

  useEffect(() => {
    if (!selectedDecisionSymbol) {
      setSymbolExplainability(null);
      return;
    }
    loadSymbolExplainability(selectedDecisionSymbol);
  }, [loadSymbolExplainability, selectedDecisionSymbol]);

  useEffect(() => {
    const timer = setInterval(() => {
      load({ silent: true });
    }, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const onSelectDecisionCard = async (symbol) => {
    setSelectedDecisionSymbol(symbol);
    await loadSymbolExplainability(symbol);
    setIsExplainabilityDrawerOpen(true);
  };

  const openSymbolDetail = (symbol) => {
    navigate(`/user/symbol/${encodeURIComponent(symbol)}`);
  };

  const openImpactSimulatorFromCard = (card) => {
    setImpactStrategyId(card?.top_contributors?.[0]?.strategy_id || "");
    setImpactFamily(card?.dominant_family || "");
  };

  const activeBotCount = useMemo(
    () => (botProfiles || []).filter((item) => item.is_running && item.is_enabled).length,
    [botProfiles],
  );

  const liveControlState = useMemo(() => {
    const rawMode = String(signalMode?.mode || "ASSISTED").toUpperCase();
    const latestSignal = recentSignals[0] || null;
    const currentBlocker = recentSignals.find((item) => item.status === "blocked" && item.blocked_reason_code) || null;
    let executionPath = "MANUAL_FLOW";
    if (rawMode === "AUTO" && activeBotCount > 0) {
      executionPath = "BOT_AUTO_ACTIVE";
    } else if (rawMode === "ASSISTED") {
      executionPath = "SEMI_AUTO_ACTIVE";
    }
    return {
      rawMode,
      botRuntime: activeBotCount > 0 ? "RUNNING" : "STOPPED",
      activeBotCount,
      executionPath,
      latestSignalState: latestSignal ? `${String(latestSignal.status || "-").toUpperCase()} (${latestSignal.symbol})` : "-",
      currentBlocker: currentBlocker?.blocked_reason_code || "-",
    };
  }, [activeBotCount, recentSignals, signalMode?.mode]);

  const executionModeBadge = useMemo(() => {
    const raw = String(portfolio?.live_status || "pure_live").toUpperCase();
    const tone = "border-emerald-500/60 bg-emerald-500/20 text-emerald-200";
    return { label: raw, tone };
  }, [portfolio?.live_status]);

  const setSignalModeAuto = async () => {
    toast.info("Pure Live modunda Signal Mode değiştirme kaldırıldı.");
  };

  const runFixAllBlockers = async () => {
    setIsControlBusy(true);
    try {
      const { data } = await apiClient.post("/user/signals/fix-all-blockers", null, { params: { limit: 250 } });
      toast.success(`Fix All: fixed=${data?.fixed_count || 0}, remaining=${data?.remaining_blocked || 0}`);
      await load({ silent: true });
    } catch (error) {
      toast.error(toToastMessage(error, "Fix All Blockers başarısız"));
    } finally {
      setIsControlBusy(false);
    }
  };

  const defaultPolicy = useMemo(() => {
    const auto = (riskPolicies || []).find((item) => String(item.name || "").toLowerCase().includes("starter safe (auto)"));
    return auto || (riskPolicies || [])[0] || null;
  }, [riskPolicies]);

  const riskHealth = useMemo(() => {
    // 2026-06-02 BUG-B fix: backend'ten dinamik gelirse onu kullan, fallback static.
    if (riskHealthBackend && typeof riskHealthBackend.score === "number") {
      return {
        score: riskHealthBackend.score,
        level: riskHealthBackend.level || "UNKNOWN",
        message: riskHealthBackend.message || "",
      };
    }
    return computeRiskPolicyHealth(defaultPolicy);
  }, [riskHealthBackend, defaultPolicy]);

  const wizardStorageKey = useMemo(() => (defaultPolicy ? `risk-policy-onboarding-dismissed-${defaultPolicy.id}` : ""), [defaultPolicy]);

  useEffect(() => {
    if (!defaultPolicy) {
      setWizardForm(null);
      return;
    }
    setWizardForm({
      name: defaultPolicy.name,
      position_size_pct: defaultPolicy.position_size_pct,
      daily_loss_cutoff_pct: defaultPolicy.daily_loss_cutoff_pct,
      max_open_positions: defaultPolicy.max_open_positions,
      max_leverage: defaultPolicy.max_leverage,
      risk_reward_ratio: defaultPolicy.risk_reward_ratio,
    });
  }, [defaultPolicy]);

  useEffect(() => {
    if (!wizardStorageKey) {
      setWizardDismissed(false);
      return;
    }
    setWizardDismissed(localStorage.getItem(wizardStorageKey) === "true");
  }, [wizardStorageKey]);

  const saveOnboardingPolicy = async () => {
    if (!defaultPolicy || !wizardForm) {
      return;
    }
    setIsSavingWizard(true);
    try {
      await apiClient.put(`/risk-policies/${defaultPolicy.id}`, {
        name: String(wizardForm.name || defaultPolicy.name).trim() || defaultPolicy.name,
        position_size_pct: Number(wizardForm.position_size_pct || defaultPolicy.position_size_pct),
        atr_stop_multiplier: Number(defaultPolicy.atr_stop_multiplier || 1.5),
        risk_reward_ratio: Number(wizardForm.risk_reward_ratio || defaultPolicy.risk_reward_ratio),
        daily_loss_cutoff_pct: Number(wizardForm.daily_loss_cutoff_pct || defaultPolicy.daily_loss_cutoff_pct),
        max_open_positions: Number(wizardForm.max_open_positions || defaultPolicy.max_open_positions),
        max_leverage: Number(wizardForm.max_leverage || defaultPolicy.max_leverage),
        spread_limit_bps: Number(defaultPolicy.spread_limit_bps || 30),
        slippage_limit_bps: Number(defaultPolicy.slippage_limit_bps || 40),
        min_liquidity_usdt: Number(defaultPolicy.min_liquidity_usdt || 100000),
      });
      toast.success("Onboarding risk policy güncellendi");
      if (wizardStorageKey) {
        localStorage.setItem(wizardStorageKey, "true");
      }
      setWizardDismissed(true);
      await load();
    } catch (error) {
      toast.error(toToastMessage(error, "Risk policy güncellenemedi"));
    } finally {
      setIsSavingWizard(false);
    }
  };

  const dismissWizard = () => {
    if (wizardStorageKey) {
      localStorage.setItem(wizardStorageKey, "true");
    }
    setWizardDismissed(true);
  };

  const chartData = useMemo(
    () => [
      { metric: "Capital", value: dashboard?.current_capital ?? 0 },
      { metric: "Balance", value: dashboard?.available_balance ?? 0 },
      { metric: "PnL", value: portfolio?.closed_pnl ?? 0 },
      { metric: "Win", value: performance?.win_rate ?? 0 },
    ],
    [dashboard, performance, portfolio],
  );

  if (isLoading) {
    return <LoadingSkeleton rows={6} testId="user-dashboard-loading-skeleton" />;
  }

  return (
    <section className="grid grid-cols-12 gap-4" data-testid="user-dashboard-page">
      <header className="col-span-12 border border-slate-800 bg-slate-900 p-4" data-testid="user-dashboard-header">
        <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="user-dashboard-title">User Dashboard</h2>
        <p className="mt-2 text-sm text-slate-400" data-testid="user-dashboard-description">
          Responsive ve erişilebilir özet görünümü. Assisted kuyruk, portföy ve performans tek ekranda.
        </p>
      </header>

      {defaultPolicy && wizardForm && !wizardDismissed && (
        <section className="col-span-12 rounded border border-amber-500/40 bg-amber-950/10 p-4" data-testid="user-dashboard-risk-onboarding-wizard">
          <p className="text-xs uppercase tracking-widest text-amber-300" data-testid="user-dashboard-risk-onboarding-title">Onboarding Risk Wizard</p>
          <p className="mt-1 text-sm text-amber-100" data-testid="user-dashboard-risk-onboarding-description">Başlangıç policy değerlerini hızlıca özelleştir, sonra istersen Risk Policy sayfasından detaylandır.</p>
          <div className="mt-3 grid gap-2 md:grid-cols-3" data-testid="user-dashboard-risk-onboarding-grid">
            <label className="space-y-1" data-testid="user-dashboard-risk-onboarding-name-field">
              <span className="text-xs text-amber-200">Policy Name</span>
              <Input value={wizardForm.name} onChange={(event) => setWizardForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="user-dashboard-risk-onboarding-name-input" />
            </label>
            <label className="space-y-1" data-testid="user-dashboard-risk-onboarding-position-field">
              <span className="text-xs text-amber-200">Position Size %</span>
              <Input type="number" step="0.1" value={wizardForm.position_size_pct} onChange={(event) => setWizardForm((prev) => ({ ...prev, position_size_pct: event.target.value }))} data-testid="user-dashboard-risk-onboarding-position-input" />
            </label>
            <label className="space-y-1" data-testid="user-dashboard-risk-onboarding-daily-loss-field">
              <span className="text-xs text-amber-200">Daily Loss %</span>
              <Input type="number" step="0.1" value={wizardForm.daily_loss_cutoff_pct} onChange={(event) => setWizardForm((prev) => ({ ...prev, daily_loss_cutoff_pct: event.target.value }))} data-testid="user-dashboard-risk-onboarding-daily-loss-input" />
            </label>
            <label className="space-y-1" data-testid="user-dashboard-risk-onboarding-max-open-field">
              <span className="text-xs text-amber-200">Max Open</span>
              <Input type="number" value={wizardForm.max_open_positions} onChange={(event) => setWizardForm((prev) => ({ ...prev, max_open_positions: event.target.value }))} data-testid="user-dashboard-risk-onboarding-max-open-input" />
            </label>
            <label className="space-y-1" data-testid="user-dashboard-risk-onboarding-max-leverage-field">
              <span className="text-xs text-amber-200">Max Leverage</span>
              <Input type="number" value={wizardForm.max_leverage} onChange={(event) => setWizardForm((prev) => ({ ...prev, max_leverage: event.target.value }))} data-testid="user-dashboard-risk-onboarding-max-leverage-input" />
            </label>
            <label className="space-y-1" data-testid="user-dashboard-risk-onboarding-rr-field">
              <span className="text-xs text-amber-200">Risk Reward Ratio</span>
              <Input type="number" step="0.1" value={wizardForm.risk_reward_ratio} onChange={(event) => setWizardForm((prev) => ({ ...prev, risk_reward_ratio: event.target.value }))} data-testid="user-dashboard-risk-onboarding-rr-input" />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="user-dashboard-risk-onboarding-actions">
            <Button onClick={saveOnboardingPolicy} disabled={isSavingWizard} data-testid="user-dashboard-risk-onboarding-save-button">
              {isSavingWizard ? "Kaydediliyor..." : "Kaydet ve Tamamla"}
            </Button>
            <Button variant="outline" onClick={dismissWizard} data-testid="user-dashboard-risk-onboarding-dismiss-button">Sonra Yap</Button>
          </div>
        </section>
      )}

      <div className="col-span-12 grid grid-cols-12 gap-3" data-testid="user-dashboard-metrics-grid" aria-label="Dashboard metrikleri">
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Bot" value={dashboard?.bot_count ?? "-"} testId="user-dashboard-metric-bot-count" unavailable={failedDataSources.includes("user_dashboard")} /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Running" value={dashboard?.running_bot_count ?? "-"} testId="user-dashboard-metric-running-bot-count" unavailable={failedDataSources.includes("user_dashboard")} /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Risk Policy" value={dashboard?.risk_policy_count ?? "-"} testId="user-dashboard-metric-risk-policy-count" unavailable={failedDataSources.includes("user_dashboard")} /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Open Positions" value={dashboard?.open_positions_count ?? "-"} tone="orange" testId="user-dashboard-metric-open-positions" unavailable={failedDataSources.includes("user_dashboard")} /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Pending" value={dashboard?.pending_signals_count ?? "-"} tone="orange" testId="user-dashboard-metric-pending-signals" unavailable={failedDataSources.includes("user_dashboard")} /></div>
        <div className="col-span-6 md:col-span-4 xl:col-span-2"><MetricCard label="Heartbeat" value={dashboard?.heartbeat ?? "-"} tone="blue" testId="user-dashboard-metric-heartbeat" /></div>
      </div>

      <div className="col-span-12 lg:col-span-8" data-testid="user-dashboard-chart-col">
        <ResponsiveMiniLineChart
          data={chartData}
          xKey="metric"
          yKey="value"
          title="Dashboard Snapshot"
          testId="user-dashboard-responsive-chart"
        />
      </div>

      <div className="col-span-12 lg:col-span-4 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-dashboard-summary-panel">
        <p className="text-xs uppercase tracking-widest text-slate-500" data-testid="user-dashboard-summary-title">Quick Summary</p>
        <div className="mt-2" data-testid="user-dashboard-execution-mode-badge-wrapper">
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${executionModeBadge.tone}`} data-testid="user-dashboard-execution-mode-badge">
            live_status: {executionModeBadge.label}
          </span>
        </div>
        <p className="mt-2 text-sm" data-testid="user-dashboard-current-capital">Current Capital: {dashboard?.current_capital ?? "-"}</p>
        <p className="mt-1 text-sm" data-testid="user-dashboard-available-balance">Available Balance: {dashboard?.available_balance ?? "-"}</p>
        <p className="mt-1 text-sm" data-testid="user-dashboard-closed-pnl">Closed PnL: {portfolio?.closed_pnl ?? "-"}</p>
        <p className="mt-1 text-sm" data-testid="user-dashboard-win-rate">Win Rate: {performance?.win_rate ?? "-"}</p>
        <div className="mt-3 rounded border border-cyan-900/40 bg-cyan-950/20 p-3" data-testid="user-dashboard-risk-policy-health-card">
          <p className="text-xs uppercase tracking-widest text-cyan-300" data-testid="user-dashboard-risk-policy-health-title">Risk Policy Health Score</p>
          <p className="mt-1 text-2xl font-black" data-testid="user-dashboard-risk-policy-health-score">{riskHealth.score}</p>
          <p className="text-xs text-cyan-100" data-testid="user-dashboard-risk-policy-health-level">level: {riskHealth.level}</p>
          <p className="text-xs text-cyan-100" data-testid="user-dashboard-risk-policy-health-message">{riskHealth.message}</p>
        </div>
      </div>

      <section className="col-span-12 rounded border border-cyan-800/50 bg-cyan-950/20 p-4" data-testid="user-dashboard-live-control-status-card">
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="user-dashboard-live-control-status-header">
          <p className="text-xs uppercase tracking-widest text-cyan-300" data-testid="user-dashboard-live-control-status-title">Live Control Status</p>
          <div className="flex items-center gap-2" data-testid="user-dashboard-live-control-status-badges">
            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${executionModeBadge.tone}`} data-testid="user-dashboard-live-control-execution-mode-chip">
              {executionModeBadge.label}
            </span>
            <p className="text-xs text-cyan-100" data-testid="user-dashboard-live-control-status-refresh">Auto Refresh: 10s</p>
          </div>
        </div>
        <div className="mt-3 grid gap-2 md:grid-cols-3" data-testid="user-dashboard-live-control-status-grid">
          <p className="text-sm" data-testid="user-dashboard-live-control-mode">Signal Mode: {liveControlState.rawMode}</p>
          <p className="text-sm" data-testid="user-dashboard-live-control-bot-runtime">Bot Runtime: {liveControlState.botRuntime} ({liveControlState.activeBotCount})</p>
          <p className="text-sm" data-testid="user-dashboard-live-control-execution-path">Execution Path: {liveControlState.executionPath}</p>
          <p className="text-sm" data-testid="user-dashboard-live-control-latest-signal-state">Last Signal State: {liveControlState.latestSignalState}</p>
          <p className="text-sm" data-testid="user-dashboard-live-control-current-blocker">Current Blocker: {liveControlState.currentBlocker}</p>
          <p className="text-sm" data-testid="user-dashboard-live-control-note">Not: ORDER_PRECHECK_FAILED bypass edilmez, güvenlik için blocked kalır.</p>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="user-dashboard-live-control-actions">
          <Button variant="outline" disabled={isControlBusy} onClick={setSignalModeAuto} data-testid="user-dashboard-live-control-set-auto-button">Mode Switch Kaldırıldı</Button>
          <Button className="bg-cyan-500 text-black hover:bg-cyan-400" disabled={isControlBusy} onClick={runFixAllBlockers} data-testid="user-dashboard-live-control-fix-all-blockers-button">Fix All Blockers</Button>
          <Button variant="outline" disabled={isControlBusy} onClick={() => load({ silent: true })} data-testid="user-dashboard-live-control-refresh-button">Yenile</Button>
        </div>
      </section>

      <section className="col-span-12 lg:col-span-6 rounded border border-rose-800/50 bg-rose-950/20 p-4" data-testid="user-dashboard-strategy-scoreboard-panel">
        <div className="flex items-center justify-between gap-2" data-testid="user-dashboard-strategy-scoreboard-header">
          <p className="text-xs uppercase tracking-widest text-rose-300" data-testid="user-dashboard-strategy-scoreboard-title">Strategy Quality Scoreboard</p>
          <p className="text-xs text-rose-100" data-testid="user-dashboard-strategy-scoreboard-window">Window: 7d</p>
        </div>
        <div className="mt-3 grid gap-2" data-testid="user-dashboard-strategy-scoreboard-list">
          {(strategyScoreboard?.worst_strategies || []).length === 0 && <p className="text-xs text-rose-100" data-testid="user-dashboard-strategy-scoreboard-empty">Henüz yeterli kapanmış trade yok.</p>}
          {(strategyScoreboard?.worst_strategies || []).map((item) => (
            <article key={item.strategy_id} className="border border-rose-900/50 bg-black/20 p-3" data-testid={`user-dashboard-strategy-scoreboard-item-${item.strategy_id}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-rose-100" data-testid={`user-dashboard-strategy-scoreboard-item-name-${item.strategy_id}`}>{item.strategy_id}</p>
                <span className="text-xs text-rose-200" data-testid={`user-dashboard-strategy-scoreboard-item-trades-${item.strategy_id}`}>Trades: {item.trade_count ?? 0}</span>
              </div>
              <div className="mt-2 grid gap-2 md:grid-cols-4 text-xs text-rose-100">
                <p data-testid={`user-dashboard-strategy-scoreboard-item-win-rate-${item.strategy_id}`}>Win Rate: {item.win_rate ?? '-'}</p>
                <p data-testid={`user-dashboard-strategy-scoreboard-item-profit-factor-${item.strategy_id}`}>Profit Factor: {item.profit_factor ?? '-'}</p>
                <p data-testid={`user-dashboard-strategy-scoreboard-item-expectancy-${item.strategy_id}`}>Expectancy: {item.expectancy ?? '-'}</p>
                <p data-testid={`user-dashboard-strategy-scoreboard-item-false-entry-${item.strategy_id}`}>False Entry: {item.false_entry_rate ?? '-'}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="col-span-12 lg:col-span-6 rounded border border-emerald-800/50 bg-emerald-950/20 p-4" data-testid="user-dashboard-funnel-panel">
        <div className="flex items-center justify-between gap-2" data-testid="user-dashboard-funnel-header">
          <p className="text-xs uppercase tracking-widest text-emerald-300" data-testid="user-dashboard-funnel-title">Signal → Trade Funnel</p>
          <p className="text-xs text-emerald-100" data-testid="user-dashboard-funnel-window">Window: 7d</p>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-5" data-testid="user-dashboard-funnel-grid">
          <MetricCard label="Generated" value={signalFunnel?.signals_generated ?? 0} tone="blue" testId="user-dashboard-funnel-generated" unavailable={failedDataSources.includes("signal_funnel")} />
          <MetricCard label="Filtered" value={signalFunnel?.signals_filtered ?? 0} tone="orange" testId="user-dashboard-funnel-filtered" unavailable={failedDataSources.includes("signal_funnel")} />
          <MetricCard label="Selected" value={signalFunnel?.signals_selected ?? 0} tone="neutral" testId="user-dashboard-funnel-selected" unavailable={failedDataSources.includes("signal_funnel")} />
          <MetricCard label="Executed" value={signalFunnel?.signals_executed ?? 0} tone="blue" testId="user-dashboard-funnel-executed" unavailable={failedDataSources.includes("signal_funnel")} />
          <MetricCard label="Profitable" value={signalFunnel?.signals_profitable ?? 0} tone="orange" testId="user-dashboard-funnel-profitable" unavailable={failedDataSources.includes("signal_funnel")} />
        </div>
      </section>

      <section className="col-span-12 rounded border border-violet-800/50 bg-violet-950/20 p-4" data-testid="user-dashboard-exit-quality-panel">
        <div className="flex items-center justify-between gap-2" data-testid="user-dashboard-exit-quality-header">
          <p className="text-xs uppercase tracking-widest text-violet-300" data-testid="user-dashboard-exit-quality-title">Exit Quality Analytics</p>
          <p className="text-xs text-violet-100" data-testid="user-dashboard-exit-quality-window">Window: 7d</p>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="user-dashboard-exit-quality-grid">
          <MetricCard label="TP Detected" value={exitQuality?.tp_detected_count ?? 0} tone="blue" testId="user-dashboard-exit-quality-detected" unavailable={failedDataSources.includes("exit_quality")} />
          <MetricCard label="TP Submit" value={exitQuality?.tp_submit_count ?? 0} tone="neutral" testId="user-dashboard-exit-quality-submit" unavailable={failedDataSources.includes("exit_quality")} />
          <MetricCard label="TP Filled" value={exitQuality?.tp_filled_count ?? 0} tone="blue" testId="user-dashboard-exit-quality-filled" unavailable={failedDataSources.includes("exit_quality")} />
          <MetricCard label="TP Missed" value={exitQuality?.tp_missed_count ?? 0} tone="red" testId="user-dashboard-exit-quality-missed" unavailable={failedDataSources.includes("exit_quality")} />
          <MetricCard label="Detect→Submit ms" value={exitQuality?.tp_detect_to_submit_ms ?? '-'} tone="orange" testId="user-dashboard-exit-quality-detect-submit" />
          <MetricCard label="Submit→Fill ms" value={exitQuality?.tp_submit_to_fill_ms ?? '-'} tone="orange" testId="user-dashboard-exit-quality-submit-fill" />
        </div>
      </section>

      <section className="col-span-12 lg:col-span-6 rounded border border-fuchsia-800/50 bg-fuchsia-950/20 p-4" data-testid="user-dashboard-strategy-efficiency-panel">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs uppercase tracking-widest text-fuchsia-300">Strategy Efficiency Panel</p>
          <p className="text-xs text-fuchsia-100">Window: 7d</p>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="user-dashboard-strategy-efficiency-grid">
          <div className="border border-fuchsia-900/50 p-3" data-testid="user-dashboard-strategy-efficiency-top-pnl">Top PnL: {strategyEfficiency?.rankings?.top_strategy_pnl?.strategy_id || '-'}</div>
          <div className="border border-fuchsia-900/50 p-3" data-testid="user-dashboard-strategy-efficiency-worst-pnl">Worst PnL: {strategyEfficiency?.rankings?.worst_strategy_pnl?.strategy_id || '-'}</div>
          <div className="border border-fuchsia-900/50 p-3" data-testid="user-dashboard-strategy-efficiency-most-rejects">Most Rejects: {strategyEfficiency?.rankings?.most_rejects?.strategy_id || '-'}</div>
          <div className="border border-fuchsia-900/50 p-3" data-testid="user-dashboard-strategy-efficiency-most-sl-hits">Most SL Hits: {strategyEfficiency?.rankings?.most_sl_hits?.strategy_id || '-'}</div>
        </div>
      </section>

      <section className="col-span-12 lg:col-span-6 rounded border border-amber-800/50 bg-amber-950/20 p-4" data-testid="user-dashboard-reject-root-panel">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs uppercase tracking-widest text-amber-300">Reject Root Cause Panel</p>
          <p className="text-xs text-amber-100">Window: 7d</p>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-3" data-testid="user-dashboard-reject-root-grid">
          <div className="border border-amber-900/50 p-3" data-testid="user-dashboard-reject-root-by-reason">
            <p className="text-xs uppercase tracking-widest text-amber-200">By Reason</p>
            <div className="mt-2 space-y-1 text-xs text-amber-100">
              {(rejectRootCauses?.by_reason || []).slice(0, 5).map((item) => <p key={item.reason}>{item.reason}: {item.count}</p>)}
            </div>
          </div>
          <div className="border border-amber-900/50 p-3" data-testid="user-dashboard-reject-root-by-strategy">
            <p className="text-xs uppercase tracking-widest text-amber-200">By Strategy</p>
            <div className="mt-2 space-y-1 text-xs text-amber-100">
              {(rejectRootCauses?.by_strategy || []).slice(0, 5).map((item) => <p key={item.strategy_id}>{item.strategy_id}: {item.count}</p>)}
            </div>
          </div>
          <div className="border border-amber-900/50 p-3" data-testid="user-dashboard-reject-root-by-symbol">
            <p className="text-xs uppercase tracking-widest text-amber-200">By Symbol</p>
            <div className="mt-2 space-y-1 text-xs text-amber-100">
              {(rejectRootCauses?.by_symbol || []).slice(0, 5).map((item) => <p key={item.symbol}>{item.symbol}: {item.count}</p>)}
            </div>
          </div>
        </div>
      </section>

      <section className="col-span-12 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-dashboard-pnl-split-panel">
        <p className="text-xs uppercase tracking-widest text-slate-500">PnL Dashboard</p>
        <div className="mt-3 grid gap-3 md:grid-cols-3" data-testid="user-dashboard-pnl-split-grid">
          <MetricCard label="Realized PnL" value={portfolio?.closed_pnl ?? 0} tone="blue" testId="user-dashboard-pnl-split-realized" unavailable={failedDataSources.includes("user_portfolio")} />
          <MetricCard label="Unrealized PnL" value={livePositions?.total_unrealized_pnl ?? 0} tone="orange" testId="user-dashboard-pnl-split-unrealized" unavailable={failedDataSources.includes("user_live_positions")} />
          <MetricCard label="Total PnL" value={Number(portfolio?.closed_pnl || 0) + Number(livePositions?.total_unrealized_pnl || 0)} tone="neutral" testId="user-dashboard-pnl-split-total" unavailable={failedDataSources.includes("user_portfolio") || failedDataSources.includes("user_live_positions")} />
        </div>
      </section>

      <section className="col-span-12 rounded border border-blue-800/50 bg-blue-950/20 p-4" data-testid="user-dashboard-decision-card-section">
        <div className="flex items-center justify-between gap-2" data-testid="user-dashboard-decision-card-header">
          <p className="text-xs uppercase tracking-widest text-blue-300" data-testid="user-dashboard-decision-card-title">Dashboard Decision Cards</p>
          <p className="text-xs text-blue-100" data-testid="user-dashboard-decision-card-auto-refresh">Auto Refresh: 10s</p>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="user-dashboard-decision-card-grid">
          {decisionCards.length === 0 && <p className="text-xs text-blue-100" data-testid="user-dashboard-decision-card-empty">Henüz decision card yok.</p>}
          {decisionCards.map((card) => (
            <DecisionCard
              key={card.symbol}
              card={card}
              onOpenExplainability={onSelectDecisionCard}
              onOpenSymbolDetail={openSymbolDetail}
              onOpenImpactSimulator={openImpactSimulatorFromCard}
            />
          ))}
        </div>
      </section>

      <section className="col-span-12 lg:col-span-4 rounded border border-indigo-800/50 bg-indigo-950/20 p-4" data-testid="user-dashboard-learning-impact-corner">
        <p className="text-xs uppercase tracking-widest text-indigo-300" data-testid="user-dashboard-learning-impact-corner-title">Strategic Corner · Impact Simulator</p>
        <div className="mt-2" data-testid="user-dashboard-learning-impact-corner-body">
          <UserLearningImpactWidget
            symbol={selectedDecisionSymbol || ""}
            defaultStrategyId={impactStrategyId}
            defaultFamily={impactFamily}
            compact
            testIdPrefix="user-dashboard-learning-impact"
          />
        </div>
      </section>

      <ExplainabilityDrawer
        isOpen={isExplainabilityDrawerOpen}
        onOpenChange={setIsExplainabilityDrawerOpen}
        selectedSymbol={selectedDecisionSymbol}
        isLoading={isExplainabilityLoading}
        explainability={symbolExplainability}
        formatDateLabel={formatDateLabel}
      />
    </section>
  );
};